import { inject, Injectable } from '@angular/core';
import {
  Address,
  ADDRESS_EDITABLE_FIELDS,
  CreateAddressParams,
  DeleteAddressResult,
  UpdateAddressParams,
} from '../models/address.model';
import { OutcomeKind } from '../models/interaction.model';
import { AuthService } from './auth.service';
import { ChangeLogService } from './change-log.service';
import { ClockSyncService } from './clock-sync.service';
import { DatabaseService, type QuerySignal } from './database.service';
import { LoggerService } from './logger.service';

/**
 * Offline-first address records. All operations read/write the local SQLite
 * database only; changes are picked up by _sync_log tracking and propagate
 * via the WebSocket sync — no backend API calls.
 *
 * Interaction-derived columns (`last_interaction_at`, `last_outcome_kind`)
 * are maintained by InteractionService through {@link applyInteractionSummary}
 * and {@link recomputeInteractionSummary}. Do-not-contact is a standing
 * org-scoped instruction on the address — see the design doc §6.3.
 */
@Injectable({
  providedIn: 'root',
})
export class AddressService {
  private db = inject(DatabaseService);
  private clockSync = inject(ClockSyncService);
  private authService = inject(AuthService);
  private changeLog = inject(ChangeLogService);
  private logger = inject(LoggerService).getLogger('address');

  /** All non-deleted addresses, most recently interacted-with first. */
  async getAll(): Promise<Address[]> {
    await this.db.init();
    const results = await this.db.exec(
      `SELECT * FROM addresses WHERE deleted_at IS NULL
       ORDER BY COALESCE(last_interaction_at, 0) DESC, updated_at DESC`,
    );
    return results as Address[];
  }

  /**
   * Case-insensitive substring search over the formatted address and
   * business name. An empty term returns everything.
   */
  async search(term: string): Promise<Address[]> {
    const trimmed = term.trim();
    if (!trimmed) return this.getAll();

    await this.db.init();
    const like = `%${trimmed}%`;
    const results = await this.db.exec(
      `SELECT * FROM addresses
       WHERE deleted_at IS NULL AND (formatted_address LIKE ? OR business_name LIKE ?)
       ORDER BY COALESCE(last_interaction_at, 0) DESC, updated_at DESC`,
      [like, like],
    );
    return results as Address[];
  }

  /** A single address by ID. Returns `null` if not found or deleted. */
  async getById(id: string): Promise<Address | null> {
    await this.db.init();
    const results = await this.db.exec(
      'SELECT * FROM addresses WHERE id = ? AND deleted_at IS NULL',
      [id],
    );
    return results.length > 0 ? (results[0] as Address) : null;
  }

  /**
   * The local address already linked to a registry unit, if any — used by the map (phase 3)
   * to avoid creating a duplicate row when the same registry unit is tapped again.
   */
  async getByRegistryId(registryId: string): Promise<Address | null> {
    await this.db.init();
    const results = await this.db.exec(
      'SELECT * FROM addresses WHERE registry_id = ? AND deleted_at IS NULL',
      [registryId],
    );
    return results.length > 0 ? (results[0] as Address) : null;
  }

  /**
   * Every local address already created for a registry building — used by the map's
   * multi-unit picker to show which of a building's units this org already has history
   * with, versus which are still new.
   */
  async getAllByBuildingId(buildingId: string): Promise<Address[]> {
    await this.db.init();
    const results = await this.db.exec(
      'SELECT * FROM addresses WHERE building_id = ? AND deleted_at IS NULL',
      [buildingId],
    );
    return results as Address[];
  }

  /** Reactive query for all non-deleted addresses. */
  createQuery(): QuerySignal<Address[]> {
    return this.db.createQuery('addresses', () => this.getAll());
  }

  /**
   * Addresses with no registry building match (`building_id = ''`) -- dropped pins (manual
   * or search-resolved) and rural addresses the registry has no footprint for. The map (§9.5
   * "Pins") renders these as standalone markers since there's no polygon to highlight them.
   */
  async getAllWithoutBuilding(): Promise<Address[]> {
    await this.db.init();
    const results = await this.db.exec(
      `SELECT * FROM addresses
       WHERE deleted_at IS NULL AND building_id = '' AND lat IS NOT NULL AND lng IS NOT NULL`,
    );
    return results as Address[];
  }

  /** Reactive query for {@link getAllWithoutBuilding}. */
  createFootprintlessQuery(): QuerySignal<Address[]> {
    return this.db.createQuery('addresses', () => this.getAllWithoutBuilding());
  }

  /** Reactive query for a single address by ID. */
  createQueryById(id: string): QuerySignal<Address | null> {
    return this.db.createQuery('addresses', () => this.getById(id), null);
  }

  /** Create a new address. */
  async create(params: CreateAddressParams): Promise<Address> {
    const userId = this.requireUser('create');
    await this.db.init();
    const id = this.db.generateId();
    const now = this.clockSync.now();

    this.logger.debug('Creating address:', { id, formatted_address: params.formatted_address });

    await this.db.run(
      `INSERT INTO addresses (
         id, formatted_address, unit, house_number, street, city, region, postal_code, country,
         place_type, business_name, lat, lng, geocode_status, notes, registry_id, building_id,
         created_by, created_at, updated_by, updated_at
       ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        params.formatted_address,
        params.unit ?? '',
        params.house_number ?? '',
        params.street ?? '',
        params.city ?? '',
        params.region ?? '',
        params.postal_code ?? '',
        params.country ?? '',
        params.place_type ?? 'unknown',
        params.business_name ?? '',
        params.lat ?? null,
        params.lng ?? null,
        params.geocode_status ?? 'manual',
        params.notes ?? '',
        params.registry_id ?? null,
        params.building_id ?? '',
        userId,
        now,
        userId,
        now,
      ],
    );

    const created = await this.getById(id);
    if (!created) throw new Error('Failed to retrieve address after creation');
    return created;
  }

  /**
   * Update editable fields. Omitted fields keep their current value. Writes a
   * change_log entry for whatever actually changed and marks the row as
   * locally edited so registry projection (phase 2) won't clobber it.
   */
  async update(params: UpdateAddressParams): Promise<Address> {
    const userId = this.requireUser('update');
    await this.db.init();
    const existing = await this.getById(params.id);
    if (!existing) {
      throw new Error(`Address '${params.id}' not found`);
    }

    const next: Address = { ...existing };
    for (const field of ADDRESS_EDITABLE_FIELDS) {
      const value = params[field];
      if (value !== undefined) {
        Object.assign(next, { [field]: value });
      }
    }

    const changes = this.changeLog.diff(existing, next, ADDRESS_EDITABLE_FIELDS);
    if (!changes) {
      this.logger.debug('Address update was a no-op:', params.id);
      return existing;
    }

    const now = this.clockSync.now();
    const setClause = ADDRESS_EDITABLE_FIELDS.map((f) => `${f} = ?`).join(', ');
    await this.db.run(
      `UPDATE addresses SET ${setClause}, is_locally_edited = 1, updated_by = ?, updated_at = ? WHERE id = ?`,
      [...ADDRESS_EDITABLE_FIELDS.map((f) => next[f]), userId, now, params.id],
    );
    await this.changeLog.record('address', params.id, existing, next, ADDRESS_EDITABLE_FIELDS);

    const updated = await this.getById(params.id);
    if (!updated) throw new Error('Failed to retrieve address after update');
    return updated;
  }

  /** Flag an address do-not-contact. Idempotent; logs the change. */
  async setDoNotContact(id: string, reason: string): Promise<void> {
    const userId = this.requireUser('flag');
    await this.db.init();
    const existing = await this.getById(id);
    if (!existing) throw new Error(`Address '${id}' not found`);

    const now = this.clockSync.now();
    await this.db.run(
      `UPDATE addresses SET do_not_contact = 1, do_not_contact_reason = ?, do_not_contact_at = ?,
         do_not_contact_by = ?, updated_by = ?, updated_at = ? WHERE id = ?`,
      [reason, now, userId, userId, now, id],
    );
    await this.changeLog.record(
      'address',
      id,
      existing,
      { ...existing, do_not_contact: 1, do_not_contact_reason: reason },
      ['do_not_contact', 'do_not_contact_reason'],
    );
    this.logger.info('Address flagged do-not-contact:', id);
  }

  /** Clear a do-not-contact flag. Requires `address.manage-dnc` at the UI layer. */
  async clearDoNotContact(id: string): Promise<void> {
    const userId = this.requireUser('unflag');
    await this.db.init();
    const existing = await this.getById(id);
    if (!existing) throw new Error(`Address '${id}' not found`);

    const now = this.clockSync.now();
    await this.db.run(
      `UPDATE addresses SET do_not_contact = 0, do_not_contact_reason = '', do_not_contact_at = NULL,
         do_not_contact_by = '', updated_by = ?, updated_at = ? WHERE id = ?`,
      [userId, now, id],
    );
    await this.changeLog.record(
      'address',
      id,
      existing,
      { ...existing, do_not_contact: 0, do_not_contact_reason: '' },
      ['do_not_contact', 'do_not_contact_reason'],
    );
    this.logger.info('Address do-not-contact cleared:', id);
  }

  /**
   * Called by InteractionService after an interaction is logged. Only moves
   * the summary forward — an interaction back-dated before the current
   * `last_interaction_at` leaves it untouched.
   */
  async applyInteractionSummary(
    addressId: string,
    occurredAt: number,
    outcomeKind: OutcomeKind,
    objectionId = '',
  ): Promise<void> {
    await this.db.init();
    const existing = await this.getById(addressId);
    if (!existing) return;
    if (existing.last_interaction_at !== null && existing.last_interaction_at > occurredAt) {
      return;
    }
    await this.db.run(
      'UPDATE addresses SET last_interaction_at = ?, last_outcome_kind = ?, last_objection_id = ? WHERE id = ?',
      [occurredAt, outcomeKind, objectionId, addressId],
    );
  }

  /**
   * Recompute the summary from the address's remaining interactions — used
   * after an interaction is deleted or its outcome edited.
   */
  async recomputeInteractionSummary(addressId: string): Promise<void> {
    await this.db.init();
    const rows = (await this.db.exec(
      `SELECT occurred_at, outcome_kind, objection_id FROM interactions
       WHERE address_id = ? AND deleted_at IS NULL ORDER BY occurred_at DESC LIMIT 1`,
      [addressId],
    )) as { occurred_at: number; outcome_kind: OutcomeKind; objection_id: string }[];
    const latest = rows[0];
    await this.db.run(
      'UPDATE addresses SET last_interaction_at = ?, last_outcome_kind = ?, last_objection_id = ? WHERE id = ?',
      [latest?.occurred_at ?? null, latest?.outcome_kind ?? '', latest?.objection_id ?? '', addressId],
    );
  }

  /**
   * Soft-delete an address. Refused if it has any (non-deleted) interactions
   * logged against it -- those are the history a delete would orphan, so the
   * caller should redirect the user to remove them first, or this shouldn't
   * have been offered as an option (see `address.delete` and creator-owned
   * access in the UI layer, e.g. AddressFormComponent).
   */
  async delete(id: string): Promise<DeleteAddressResult> {
    const userId = this.requireUser('delete');
    await this.db.init();
    const existing = await this.getById(id);
    if (!existing) return { success: false, reason: 'not_found' };

    const counted = (await this.db.exec(
      'SELECT COUNT(*) as count FROM interactions WHERE address_id = ? AND deleted_at IS NULL',
      [id],
    )) as { count: number }[];
    if ((counted[0]?.count ?? 0) > 0) return { success: false, reason: 'has_interactions' };

    const now = this.clockSync.now();
    await this.db.run(
      'UPDATE addresses SET deleted_at = ?, updated_by = ?, updated_at = ? WHERE id = ?',
      [now, userId, now, id],
    );
    this.logger.info('Address deleted:', id);
    return { success: true };
  }

  private requireUser(action: string): string {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      throw new Error(`User must be authenticated to ${action} an address`);
    }
    return userId;
  }
}
